import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, IndianRupee, DollarSign, Loader2, ShieldCheck, Info, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGeo } from "@/lib/geo";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

const FEATURES = [
  "Full 4-step exercise plan (Loosen · Stretch · Wake Up · Move Well)",
  "All 8 posture patterns unlocked",
  "Full muscle assessment with per-muscle diagrams + instructions",
  "AI physio chat assistant",
  "Video demos for every exercise",
  "Save your assessments + progress chart",
  "Printable PDF report",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isIndia, ready: geoReady } = useGeo();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cf, setCf] = useState(null);

  useEffect(() => {
    http.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!cfg?.env) return;
    loadCashfree({ mode: cfg.env }).then(setCf).catch((err) => {
      console.error("Cashfree SDK load failed:", err);
      toast.error("Payment system failed to load. Please refresh.");
    });
  }, [cfg?.env]);

  const pay = async () => {
    if (!user) return navigate("/login", { state: { from: "/pricing" } });
    if (phone && !phone.match(/^[0-9]{10,15}$/)) return toast.error("Enter a valid phone number or leave empty");
    if (!cfg?.configured) return toast.error("Payments not configured yet. Please contact support.");
    if (!cf) return toast.error("Payment system still loading — please wait a moment and try again.");
    setBusy(true);
    try {
      const body = {};
      if (phone) body.phone = phone;
      if (!isIndia) body.currency = "USD";
      const { data } = await http.post("/payments/create-order", body);
      const result = await cf.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });
      if (result?.error) throw new Error(result.error.message || "Checkout failed");
    } catch (e) {
      console.error("Cashfree checkout error:", e);
      toast.error(e.response?.data?.detail || e.message || "Could not start payment");
    } finally { setBusy(false); }
  };

  const isAdmin = user?.role === "admin";
  const hasAccess = user?.has_access;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      <div className="text-center mb-10">
        <Badge className="bg-honey text-forest border-0 mb-3">Limited-time launch price</Badge>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-forest mb-3">
          {isIndia ? (
            <>Get your full posture plan for <span className="line-through text-muted-foreground text-3xl">₹499</span> ₹199</>
          ) : (
            <>Get your full posture plan for <span className="line-through text-muted-foreground text-3xl">$5</span> $2</>
          )}
        </h1>
        <p className="text-lg text-muted-foreground">
          One payment. 3 months of full access. No auto-renewal.
        </p>
      </div>

      {/* International PayPal info banner — ONLY for non-India users */}
      {geoReady && !isIndia && !isAdmin && !hasAccess && (
        <div
          className="mb-6 rounded-xl border border-sage/40 bg-sage/10 p-5 flex items-start gap-4"
          data-testid="paypal-info-banner"
        >
          <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-sage" />
          </div>
          <div className="text-sm text-forest">
            <p className="font-medium mb-1">Pay with PayPal or any international card</p>
            <p className="text-forest/80 mb-2">
              When you click Pay, you'll go to our secure checkout. There you can pay with:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-xs">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sage" /> PayPal balance or account</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sage" /> Visa / Mastercard / Amex</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sage" /> Apple Pay / Google Pay</li>
            </ul>
            <p className="text-xs text-forest/70 mt-2">
              Amount shown at checkout: <strong>$2 USD</strong>. Your bank handles any small FX conversion.
            </p>
          </div>
        </div>
      )}

      <Card className="p-8 bg-white border-forest/10 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sage mb-2">3-Month Full Access</p>
            <div className="flex items-baseline gap-1 mb-4">
              {isIndia ? (
                <>
                  <IndianRupee className="w-8 h-8 text-forest" />
                  <span className="font-heading text-6xl font-bold text-forest">199</span>
                </>
              ) : (
                <>
                  <DollarSign className="w-8 h-8 text-forest" />
                  <span className="font-heading text-6xl font-bold text-forest">2</span>
                </>
              )}
              <span className="text-muted-foreground ml-2">/ 3 months</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Full unlimited access — every posture pattern, muscle test, exercise plan, chat, and progress tracking.
            </p>
            <ul className="space-y-2 text-sm">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-sage shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <div>
            {isAdmin ? (
              <div className="bg-forest text-bone p-6 rounded-lg text-center">
                <p className="font-heading text-lg mb-2">You have admin access</p>
                <p className="text-sm opacity-90">All features unlocked. No payment needed.</p>
              </div>
            ) : hasAccess ? (
              <div className="bg-sage text-white p-6 rounded-lg text-center">
                <p className="font-heading text-lg mb-1">You're all set!</p>
                <p className="text-sm opacity-90 mb-3">Your access is active.</p>
                <Button onClick={() => navigate("/")} variant="outline" className="border-white text-white hover:bg-white/10">
                  Go to dashboard
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {isIndia && (
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Phone number <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <Input
                      placeholder="10-digit mobile number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                      maxLength={15}
                      data-testid="pay-phone"
                    />
                  </div>
                )}
                <Button
                  onClick={pay}
                  disabled={busy || !cf || !geoReady}
                  size="lg"
                  className="w-full bg-forest hover:bg-forest/90 text-bone rounded-full h-12"
                  data-testid="pay-btn"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                  ) : isIndia ? (
                    <>Pay ₹199 · Unlock now</>
                  ) : (
                    <>Pay $2 · Unlock now</>
                  )}
                </Button>
                {!cfg?.configured && (
                  <p className="text-xs text-terracotta text-center">
                    Payment gateway not connected yet. Contact support if you see this.
                  </p>
                )}
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
                  <ShieldCheck className="w-3 h-3" />
                  {isIndia ? "Secure checkout · UPI, cards, netbanking" : "Secure checkout · PayPal & cards"}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            title: "One-time payment",
            body: isIndia
              ? "No auto-renewal. Pay ₹199 once, use for 90 days."
              : "No auto-renewal. Pay $2 once, use for 90 days.",
          },
          {
            title: "No hidden fees",
            body: "Secure checkout. All-inclusive price.",
          },
          {
            title: "Money-back guarantee",
            body: "Not satisfied in 7 days? Contact us for a refund.",
          },
        ].map((f, i) => (
          <div key={i} className="bg-white border border-forest/10 rounded-lg p-4 text-sm">
            <p className="font-heading font-semibold text-forest mb-1">{f.title}</p>
            <p className="text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>

      <p className="text-center mt-8 text-xs text-muted-foreground">
        Apexture is an educational fitness & posture-awareness platform. It is not a substitute for
        professional medical care. See our <Link to="/disclaimer" className="underline">Medical Disclaimer</Link>
        {isIndia && <> and <Link to="/refund" className="underline">Refund Policy</Link></>}.
      </p>
    </div>
  );
}
